export function buildOrderSummary(order) {
  if (!order) {
    return {
      ordered_cases: 0,
      shipped_cases: 0,
      returned_cases: 0,
      remaining_cases: 0,
    };
  }

  if (
    order.ordered_cases != null ||
    order.shipped_cases != null ||
    order.remaining_cases != null
  ) {
    const ordered = Number(order.ordered_cases ?? 0);
    const shipped = Number(order.shipped_cases ?? 0);
    const returned = Number(order.returned_cases ?? 0);
    const remaining =
      order.remaining_cases != null
        ? Number(order.remaining_cases)
        : Math.max(0, ordered - shipped);

    return {
      ordered_cases: ordered,
      shipped_cases: shipped,
      returned_cases: returned,
      remaining_cases: remaining,
    };
  }

  const products = order.products ?? [];
  let ordered = 0;
  let shipped = 0;
  let returned = 0;

  products.forEach((item) => {
    const line = getLineFulfillment(item);
    ordered += line.ordered;
    shipped += line.shipped;
    returned += line.returned;
  });

  return {
    ordered_cases: ordered,
    shipped_cases: shipped,
    returned_cases: returned,
    remaining_cases: Math.max(0, ordered - shipped),
  };
}

export function getLineFulfillment(item) {
  const ordered = parseFloat(item?.quantity) || 0;
  const shipped =
    parseFloat(item?.shipped_quantity ?? item?.shipped_amount ?? 0) || 0;
  const returned = parseFloat(item?.returned_quantity ?? 0) || 0;
  const remaining =
    item?.remaining_quantity != null
      ? parseFloat(item.remaining_quantity) || 0
      : Math.max(0, ordered - shipped);

  return { ordered, shipped, returned, remaining };
}

export function resolveOrderStatusValue(order) {
  if (!order) return null;
  if (order.status?.value != null) return order.status.value;
  return order.status;
}

/** Cancelled = 11, Completed = 6 */
export function canCreateShipment(order, summary) {
  if (!order) return false;

  const status = resolveOrderStatusValue(order);
  if (status === 11) return false;

  const remaining = summary?.remaining_cases ?? buildOrderSummary(order).remaining_cases;
  if (status === 6 && remaining <= 0) return false;

  return true;
}

export function formatShipmentStatus(status) {
  if (!status) return "Draft";

  const labels = {
    draft: "Draft",
    picking: "Picking",
    picked: "Picked",
    loaded: "Loaded",
    shipped: "Shipped",
    delivered: "Delivered",
    voided: "Voided",
  };

  const key = String(status).toLowerCase();
  return labels[key] || status;
}

export function getShipmentStatusBadgeClass(status) {
  const baseClass = "rounded-full px-3 py-1 text-xs font-semibold";
  const key = String(status || "draft").toLowerCase();

  const classes = {
    draft: `${baseClass} bg-slate-100 text-slate-700`,
    picking: `${baseClass} bg-blue-50 text-blue-700`,
    picked: `${baseClass} bg-blue-50 text-blue-700`,
    loaded: `${baseClass} bg-amber-50 text-amber-700`,
    shipped: `${baseClass} bg-green-50 text-green-700`,
    delivered: `${baseClass} bg-teal-50 text-teal-700`,
    voided: `${baseClass} bg-red-50 text-red-700`,
  };

  return classes[key] || classes.draft;
}

export function formatCreditStatus(status) {
  const labels = {
    not_credited: "Not Credited",
    pending_credit: "Pending Credit",
    partial_credit: "Partial Credit",
    credited: "Credited",
  };

  const key = String(status || "not_credited").toLowerCase();
  return labels[key] || "Not Credited";
}

export function getCreditStatusBadgeClass(status) {
  const baseClass = "rounded-full px-3 py-1 text-xs font-semibold";
  const key = String(status || "not_credited").toLowerCase();

  const classes = {
    not_credited: `${baseClass} bg-slate-100 text-slate-700`,
    pending_credit: `${baseClass} bg-amber-50 text-amber-700`,
    partial_credit: `${baseClass} bg-amber-50 text-amber-700`,
    credited: `${baseClass} bg-green-50 text-green-700`,
  };

  return classes[key] || classes.not_credited;
}

export function getShipmentActionLabel(status) {
  const key = String(status || "").toLowerCase();
  if (key === "shipped" || key === "delivered") return "View Shipment";
  return "Continue Picking";
}

export function formatCases(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
